import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';

import { DatePicker } from '@mui/x-date-pickers';
import { subDays, startOfDay, endOfDay, format } from 'date-fns';

import axiosInstance from 'utils/axiosUtil';
import { withRouter } from 'utils';
import { TitleTooltip, SmallIconTextButton, DynamicTable } from 'components';
import { listMileageLogs } from 'store/actions/mileageActions';
import { listActiveUsers } from 'store/actions/usersActions';
import { showConfirmationDialog } from 'store/actions/systemActions';
import { StoreUtil } from 'doctivity-shared/utils';

import MileageDialog from './MileageDialog';

const minimumStartDate = new Date(2018, 0, 1);

// ── sessionStorage helpers ─────────────────────────────────────────────────────
const isValidDate = (d) => d instanceof Date && !isNaN(d);

function readSessionDates() {
  const startRaw = sessionStorage.getItem('startDate');
  const endRaw   = sessionStorage.getItem('endDate');
  const start    = startRaw ? new Date(startRaw) : null;
  const end      = endRaw   ? new Date(endRaw)   : null;
  return {
    startDate: startOfDay(isValidDate(start) ? start : subDays(new Date(), 30)),
    endDate:   endOfDay(isValidDate(end) ? end : new Date()),
  };
}

function readSessionUser() {
  const raw = sessionStorage.getItem('filterUser');
  try {
    return raw ? JSON.parse(raw) : 'All Users';
  } catch {
    return 'All Users';
  }
}

const styles = (theme) => ({
  // ── Content wrapper ────────────────────────────────────────────────────────
  content: {
    padding: theme.spacing(3),
  },

  // ── Filter bar (matches ProviderCommentsOptions) ───────────────────────────
  filters: {
    '& > *': {
      marginBottom: theme.spacing(1),
    },
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  filterSpace: {
    width: 4,
  },
  userFilter: {
    minWidth: 270,
  },
  rangeControl: {
    minWidth: 90,
  },

  // ── Card spacing ───────────────────────────────────────────────────────────
  sectionCard: {
    marginBottom: theme.spacing(3),
  },

  // ── Card header row ────────────────────────────────────────────────────────
  cardHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  spacer: {
    flex: 1,
  },

});

// ── Date formatting ────────────────────────────────────────────────────────────
// Safely formats a DATEONLY string ('YYYY-MM-DD') as 'MM/DD/YYYY' without
// timezone conversion (avoids off-by-one on systems west of UTC).
function formatDate(dateStr) {
  if (!dateStr) return '—';
  const parts = String(dateStr).split('T')[0].split('-');
  if (parts.length !== 3) return dateStr;
  const [year, month, day] = parts;
  return `${month}/${day}/${year}`;
}

// ── Client-side sort helper ────────────────────────────────────────────────────
// Accepts DynamicTable's `query.order` format: [[field, 'ASC'|'DESC'], ...]
// Multi-segment paths (e.g. 'user.last_name') are stored as ['user', 'last_name', dir].
function sortRows(rows, order) {
  if (!order || order.length === 0) return rows;
  const primary = order[0];
  if (!primary) return rows;
  // Reconstruct field key and direction from the order entry
  const field = primary.slice(0, -1).join('.');
  const dir = (primary[primary.length - 1] || 'DESC').toLowerCase();

  return [...rows].sort((a, b) => {
    let aVal, bVal;
    switch (field) {
      case 'user.last_name':
        aVal = (a.user?.last_name  ?? '').toLowerCase();
        bVal = (b.user?.last_name  ?? '').toLowerCase();
        break;
      case 'user.first_name':
        aVal = (a.user?.first_name ?? '').toLowerCase();
        bVal = (b.user?.first_name ?? '').toLowerCase();
        break;
      case 'mileage':
        aVal = parseFloat(a.mileage) || 0;
        bVal = parseFloat(b.mileage) || 0;
        break;
      case 'reimbursement':
        aVal = parseFloat(a.reimbursement) || 0;
        bVal = parseFloat(b.reimbursement) || 0;
        break;
      default: // 'date_of_trip' — DATEONLY strings sort correctly; tiebreak by created_at
        aVal = (a.date_of_trip || '') + '|' + (a.created_at || '');
        bVal = (b.date_of_trip || '') + '|' + (b.created_at || '');
    }
    if (aVal < bVal) return dir === 'asc' ? -1 : 1;
    if (aVal > bVal) return dir === 'asc' ?  1 : -1;
    return 0;
  });
}

class MileageTrackingPage extends Component {
  constructor(props) {
    super(props);
    const { startDate, endDate } = readSessionDates();

    // If the user has never set a preference, default non-admins to their own
    // logs so they immediately see their own data rather than an empty "All Users" view.
    let filterUser = readSessionUser();
    if (!sessionStorage.getItem('filterUser') && !props.isAdmin && props.currentUser) {
      filterUser = props.currentUser;
    }

    this.state = {
      startDate,
      endDate,
      filterUser,

      exportErrors: [],
      mileageDialogOpen: false,
      query: { limit: 25, offset: 0, order: [['date_of_trip', 'DESC']] },
    };
  }

  componentDidMount() {
    const { dispatch, selectedClient } = this.props;
    if (selectedClient) dispatch(listActiveUsers(selectedClient));
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    const { dispatch, selectedClient } = this.props;
    if (selectedClient && selectedClient !== prevProps.selectedClient) {
      dispatch(listActiveUsers(selectedClient));
      this.loadData();
    }
  }

  // ── Derive ISO date strings for API calls ──────────────────────────────────
  _startStr = () => {
    const { startDate } = this.state;
    return startDate ? format(startDate, 'yyyy-MM-dd') : null;
  };

  _endStr = () => {
    const { endDate } = this.state;
    return endDate ? format(endDate, 'yyyy-MM-dd') : null;
  };

  _filterUserId = () => {
    const { filterUser } = this.state;
    return (filterUser && filterUser !== 'All Users') ? filterUser.id : null;
  };

  loadData = () => {
    const { dispatch, selectedClient } = this.props;
    if (!selectedClient) return;
    const startStr     = this._startStr();
    const endStr       = this._endStr();
    const filterUserId = this._filterUserId();

    dispatch(listMileageLogs(selectedClient, startStr, endStr, filterUserId));
  };

  onUserChange = (_event, newUser) => {
    sessionStorage.setItem('filterUser', JSON.stringify(newUser));
    this.setState(
      (prev) => ({ filterUser: newUser, query: { ...prev.query, offset: 0 } }),
      this.loadData,
    );
  };

  onStartDateChange = (newDate) => {
    if (!newDate || isNaN(newDate)) return;
    const d = startOfDay(newDate);
    sessionStorage.setItem('startDate', d.toISOString());
    this.setState(
      (prev) => ({ startDate: d, query: { ...prev.query, offset: 0 } }),
      this.loadData,
    );
  };

  onEndDateChange = (newDate) => {
    if (!newDate || isNaN(newDate)) return;
    const d = endOfDay(newDate);
    sessionStorage.setItem('endDate', d.toISOString());
    this.setState(
      (prev) => ({ endDate: d, query: { ...prev.query, offset: 0 } }),
      this.loadData,
    );
  };

  // ── Mileage ──────────────────────────────────────────────────────────────

  onMileageSaved = () => {
    this.loadData();
    this.setState({ mileageDialogOpen: false });
  };

  openMileageDialog = () => {
    this.setState({ mileageDialogOpen: true });
  };

  exportMileageCsv = async () => {
    const { selectedClient } = this.props;
    const startStr     = this._startStr();
    const endStr       = this._endStr();
    const filterUserId = this._filterUserId();
    try {
      const res = await axiosInstance.get('/v2/mileage/export', {
        params: {
          clientId: selectedClient,
          ...(startStr     ? { startDate: startStr }     : {}),
          ...(endStr       ? { endDate: endStr }         : {}),
          ...(filterUserId != null ? { filterUserId }    : {}),
        },
        responseType: 'blob',
      });
      const fromLabel = startStr ? startStr.replace(/-/g, '') : 'all';
      const toLabel   = endStr   ? endStr.replace(/-/g, '')   : 'all';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Mileage_${fromLabel}_${toLabel}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('mileage export error', err);
      this.setState({ exportErrors: [err.message || 'Mileage export failed.'] });
    }
  };

  onQueryChange = (newQuery) => {
    this.setState({ query: newQuery });
  };

  render() {
    const {
      classes,
      mileageLogs, mileageLoading,
      selectedClient,
      users,
    } = this.props;
    const {
      startDate, endDate, filterUser,
      mileageDialogOpen,
      exportErrors,
      query,
    } = this.state;

    const showingAll  = !filterUser || filterUser === 'All Users';
    const userOptions = ['All Users', ...(StoreUtil.isLoaded(users) ? StoreUtil.getData(users) : [])];

    const logs = mileageLogs || [];

    // Sorted + paginated slices — reimbursement comes pre-computed per row from the backend
    const sortedLogs = sortRows(logs, query.order);
    const pagedLogs  = sortedLogs.slice(query.offset, query.offset + query.limit);

    // DynamicTable data: pass {count, rows} so pagination shows the total correctly.
    // While loading show the placeholder spinner.
    const tableData = mileageLoading
      ? { placeholder: true, showLoading: true }
      : { count: logs.length, rows: pagedLogs };

    // Column definitions — Last Name / First Name are conditional on showingAll
    const columns = [
      ...(showingAll ? [
        {
          key: 'user.last_name',
          label: 'User',
          style: { width: 130 },
          hideForMobile: true,
          format: (_v, row) =>
            row.user
              ? `${row.user.first_name} ${row.user.last_name}`.trim()
              : '—',
        },
      ] : []),
      {
        key: 'date_of_trip',
        label: 'Date',
        style: { width: 95 },
        format: (v) => formatDate(v),
      },
      {
        key: 'trip_destination',
        label: 'Trip Destination',
        hideForMobile: true,
        sortable: false,
        format: (v) => v || '—',
      },
      {
        key: 'purpose',
        label: 'Purpose',
        hideForMobile: true,
        sortable: false,
        format: (v) => v || '—',
      },
      {
        key: 'mileage',
        label: 'Miles',
        style: { width: 75, textAlign: 'right' },
        format: (v) => v ? parseFloat(v).toFixed(1) : '—',
      },
      {
        key: 'reimbursement',
        label: 'Reimbursement',
        style: { width: 100, textAlign: 'right' },
        hideForMobile: true,
        format: (_v, row) => row.is_business
          ? `$${parseFloat(row.reimbursement || 0).toFixed(2)}`
          : '$0.00',
      },
    ];

    return (
      <>
        <title>Tracking</title>

        <div className={classes.content}>

          {/* ── Filter bar — left-aligned, matches Reports / All pages ─────── */}
          <div className={classes.filters}>
            <FormControl className={classes.userFilter}>
              <Autocomplete
                options={userOptions}
                getOptionLabel={(u) => u === 'All Users' ? 'All Users' : `${u.last_name}, ${u.first_name}`}
                isOptionEqualToValue={(option, value) =>
                  option === value ||
                  (option !== 'All Users' && value !== 'All Users' && option.id === value.id)
                }
                autoComplete
                disableClearable
                value={filterUser}
                onChange={this.onUserChange}
                renderInput={(params) => <TextField {...params} label='User' />}
              />
            </FormControl>

            <div className={classes.filterSpace} />

            <FormControl className={classes.rangeControl}>
              <DatePicker
                label='From'
                value={startDate}
                minDate={minimumStartDate}
                maxDate={endDate || new Date()}
                onChange={this.onStartDateChange}
                renderInput={(params) => <TextField {...params} />}
              />
            </FormControl>

            <div className={classes.filterSpace} />

            <FormControl className={classes.rangeControl}>
              <DatePicker
                label='To'
                value={endDate}
                minDate={startDate || minimumStartDate}
                maxDate={new Date()}
                onChange={this.onEndDateChange}
                renderInput={(params) => (
                  <TextField {...params} inputProps={{ ...params.inputProps, readOnly: true }} />
                )}
              />
            </FormControl>
          </div>

          {/* ════════════════════════════════════════════════════════════ */}
          {/* MILEAGE SECTION                                             */}
          {/* ════════════════════════════════════════════════════════════ */}

          <Card className={classes.sectionCard}>
            <CardContent>
              <div className={classes.cardHeader}>
                <TitleTooltip
                  title='Mileage'
                  onExport={this.exportMileageCsv}
                />
                <div className={classes.spacer} />
                <SmallIconTextButton
                  icon='add'
                  text='Log Mileage'
                  onClick={this.openMileageDialog}
                />
              </div>

              <DynamicTable
                columns={columns}
                data={tableData}
                query={query}
                onQueryChange={this.onQueryChange}
                onRowClick={(row) => this.props.router.navigate(`/notes/mileage/${row.id}`)}
                showFilters={false}
                noDataMessage='No mileage logged for the selected date range.'
                ContainerType={Fragment}
              />
            </CardContent>
          </Card>

        </div>

        {/* ── Mileage dialog (new log only — editing via detail page) ───── */}
        <MileageDialog
          open={mileageDialogOpen}
          onClose={() => this.setState({ mileageDialogOpen: false })}
          onSaved={this.onMileageSaved}
          clientId={selectedClient}
          isAdmin={this.props.isAdmin}
          clientUsers={this.props.clientUsers}
        />

        {/* ── Export validation errors dialog ────────────────────────────── */}
        <Dialog
          open={exportErrors.length > 0}
          onClose={() => this.setState({ exportErrors: [] })}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>Export Cannot Be Generated</DialogTitle>
          <DialogContent>
            <Typography variant='body2' gutterBottom>
              The following issues must be resolved before this data can be exported
              in CMS format:
            </Typography>
            <List dense disablePadding>
              {exportErrors.map((err, i) => (
                <ListItem key={i} disableGutters>
                  <ListItemText
                    primary={err}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => this.setState({ exportErrors: [] })}
              variant='contained'
              color='primary'
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

MileageTrackingPage.propTypes = {
  classes:        PropTypes.object.isRequired,
  dispatch:       PropTypes.func.isRequired,
  router:         PropTypes.object.isRequired,
  selectedClient: PropTypes.number,
  currentUserId:  PropTypes.number,
  currentUser:    PropTypes.object,
  isAdmin:        PropTypes.bool,
  mileageLogs:    PropTypes.array,
  mileageLoading: PropTypes.bool,
  users:          PropTypes.object,
  clientUsers:    PropTypes.array,
};

function mapStateToProps(state) {
  const u = state.user || null;
  const usersStore  = StoreUtil.get(state.users, StoreUtil.COMMON_LIST);
  const clientUsers = StoreUtil.isLoaded(usersStore) ? (usersStore.data || []) : [];
  return {
    selectedClient: state.app?.selectedClient || null,
    currentUserId:  u?.id || null,
    currentUser:    u ? { id: u.id, first_name: u.first_name, last_name: u.last_name } : null,
    isAdmin:        u?.is_admin || false,
    mileageLogs:    state.mileage?.logs?.data || [],
    mileageLoading: state.mileage?.logs?.loading || false,
    users:          state.users?.list,
    clientUsers,
  };
}

export default connect(mapStateToProps)(withRouter(withStyles(styles)(MileageTrackingPage)));
